import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import { useTheme } from '@mui/material/styles';
import { ColDef } from 'ag-grid-community';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Card from '../common/Card';
import Chips from '../common/Chips';
import ClientSideAgGrid from '../common/customAgGrid/ClientSideAgGrid';
import CustomActionButton from '../common/customAgGrid/CustomActionButton';
import CustomAgDateFilter from '../common/customAgGrid/CustomAgDateFilter';
import CustomAgMultiSelectFilter from '../common/customAgGrid/CustomAgMultiSelectFilter';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LubricationDetailsInformation from './LubricationDetailsInformation';
import { SectionHeading } from '../common/styled/SectionHeading';

interface LubricationInfo {
  vessel: string;
  manager: string;
  lastUpdatedBy: string;
  lastUpdatedOn: Date | string | any | null;
}

const LubricationProductListing = ({
  lubricationInfo,
  mode = 'view',
  products,
  editProduct,
  deleteProduct,
  loading,
  onCancel: _onCancel,
  submitting: _submitting,
  onSave: _onSave,
}: {
  lubricationInfo: LubricationInfo;
  products?: any[];
  editProduct?: (_product: any) => void;
  deleteProduct?: (_product: any) => void;
  mode?: 'view' | 'edit';
  loading?: boolean;
  onCancel?: () => void;
  submitting?: boolean;
  onSave?: () => void;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [columns, setColumns] = useState<ColDef[]>([]);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [resetFiltersSignal, setResetFiltersSignal] = useState(false);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: 'productName',
        headerName: t('table.columns.productName'),
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        width: 200,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'maker',
        headerName: t('table.columns.maker'),
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        width: 150,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'category',
        headerName: t('table.columns.category'),
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        width: 200,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'type',
        headerName: t('table.columns.type'),
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        width: 150,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'unit',
        headerName: t('table.columns.unit'),
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        width: 150,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'typeInIndicator',
        headerName: t('common.typeInIndicator'),
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        width: 200,
        floatingFilterComponent: CustomAgMultiSelectFilter,
        cellRenderer: (params: any) =>
          params.value === t('common.typeInProductApiValue') ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <ErrorRoundedIcon sx={{ color: theme.palette.icons.warning }} />
            </Box>
          ) : (
            '-'
          ),
      },
      {
        field: 'updatedBy',
        headerName: t('table.columns.lastUpdatedBy'),
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        width: 200,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'updatedOn',
        headerName: t('table.columns.lastUpdatedOn'),
        sortable: true,
        filter: 'agDateColumnFilter',
        floatingFilter: true,
        width: 170,
        floatingFilterComponent: CustomAgDateFilter,
        filterParams: {
          suppressAndOrCondition: true,
          maxNumConditions: 1,
          filterOptions: ['equals'],
          defaultOption: 'equals',
        },
      },
      {
        field: 'status',
        headerName: t('common.status'),
        width: 150,
        maxWidth: 150,
        filter: true,
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
        cellRenderer: (params: any) => <Chips width="100px" label={params?.data?.status} />,
      },
    ],
    [theme, t],
  );

  useEffect(() => {
    setColumns(columnDefs);
  }, [columnDefs]);

  useEffect(() => {
    if (mode === 'edit') {
      setColumns((prevColumns) => {
        // Check if actions column already exists
        const hasActionsColumn = prevColumns.some((col) => col.field === 'actions');
        if (hasActionsColumn) {
          return prevColumns;
        }

        return [
          ...prevColumns,
          {
            field: 'actions',
            headerName: '',
            width: 50,
            maxWidth: 50,
            sortable: false,
            filter: false,
            pinned: 'right',
            cellRenderer: (params: any) => (
              <CustomActionButton
                menuOptions={[
                  {
                    label: t('buttons.edit'),
                    onClick: () => editProduct?.(params.data),
                  },
                  {
                    label: t('buttons.delete'),
                    onClick: () => deleteProduct?.(params.data),
                  },
                ]}
              />
            ),
          },
        ];
      });
    } else {
      // Remove actions column when not in edit mode
      setColumns((prevColumns) => prevColumns.filter((col) => col.field !== 'actions'));
    }
  }, [mode, editProduct, deleteProduct, t]);

  const handleClearFilters = () => {
    setResetFiltersSignal((prev) => !prev);
  };

  return (
    <Stack sx={{ width: '100%', flex: 1, minHeight: 0 }} gap={2}>
      <LubricationDetailsInformation lubricationInfo={lubricationInfo} />

      <Card sx={{ flex: 1, minHeight: 250, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            mb: 1.5,
            mt: -1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <SectionHeading>{t('common.productItems')}</SectionHeading>

          {isFilterActive && (
            <Typography
              onClick={handleClearFilters}
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: theme.palette.primary.main,
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {t('filters.clearAll')}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            height: '2px',
            backgroundColor: theme.palette.dropdown.border,
            mx: '-24px',
            // mb: 1,
            flexShrink: 0,
          }}
        />

        <Box className="ag-theme-alpine" sx={{ flex: 1, minHeight: 0 }}>
          <ClientSideAgGrid
            rowData={products}
            columnDefs={columns}
            loading={loading}
            onFiltersActiveChange={(active: boolean) => {
              setIsFilterActive(active);
              setResetFiltersSignal(false);
            }}
            resetFiltersSignal={resetFiltersSignal}
            className="lubrication-product-listing-grid"
            infiniteRows={true}
          />
        </Box>
      </Card>
    </Stack>
  );
};

export default LubricationProductListing;
